import React from "react";

export function Privacy() {
  return (
    <div className="legal-page">
      <article className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 7, 2026</p>
        <p>
          NumisTrail respects your privacy. This Policy explains how we collect,
          use, share, and protect personal data when you use the Service.
        </p>

        <ol className="legal-list">
          <li>
            <h2>Who We Are</h2>
            <p>
              Storychain Labs LLC (Oregon, USA), doing business as NumisTrail, is
              the controller of personal data processed through the Service.
            </p>
            <p>
              Contact: privacy@numistrail.com. If a European Union (EU) or United
              Kingdom (UK) representative is required, we will appoint one and
              update this Policy with their details.
            </p>
          </li>
          <li>
            <h2>Information We Collect</h2>
            <p>Account data (email, NumisTrail ID, password hash).</p>
            <p>
              Business profile data (company, role, optional phone) for partner
              or organization accounts.
            </p>
            <p>
              Billing and subscription data (billing contact details, plan and
              invoice records) when paid features are enabled. Payment card data
              is processed by our payment provider and is not stored in full by
              NumisTrail.
            </p>
            <p>Legal acceptance data (timestamp, IP address, user agent).</p>
            <p>Verification and security data (email tokens, reset events).</p>
            <p>
              Pending claim data (email and certificate details) when a
              certification entity assigns a Digital Record to a new user.
            </p>
            <p>Usage data (device, browser, IP address, interaction logs).</p>
            <p>
              Collectible and Digital Record data (certification metadata,
              ownership history, notes, exposure events, verification sessions,
              trust origin state such as USER_GENERATED or TRUSTED_CERTIFIED, and
              item edit or promotion events).
            </p>
            <p>
              Attribution data related to digital certification actions
              (certifying company identifiers and associated actor identifiers
              where applicable).
            </p>
            <p>
              Local storage identifiers for essential functions (authentication
              and language preferences).
            </p>
            <p>We do not sell personal data or use it for advertising.</p>
          </li>
          <li>
            <h2>How We Use Data and Legal Bases</h2>
            <p>
              Provide and operate the Service (performance of a contract).
            </p>
            <p>
              Secure the Service, prevent fraud, and maintain integrity
              (legitimate interests).
            </p>
            <p>
              Classify and present trust state (for example, user-generated vs
              trusted-certified) and maintain auditable certification and edit
              history (performance of a contract and legitimate interests).
            </p>
            <p>
              Review and execute operational actions on USER_GENERATED records,
              including modification, deletion, or reassignment within the
              platform, when necessary for integrity, fraud prevention, dispute
              handling, legal compliance, or security (legitimate interests and
              legal obligation).
            </p>
            <p>
              Comply with legal obligations, including recordkeeping (legal
              obligation).
            </p>
            <p>
              Communicate transactional messages about your account or actions
              (contract or legitimate interests).
            </p>
            <p>
              Send claim emails and activate pending records at the request of
              certification entities (contract or legitimate interests).
            </p>
            <p>
              Process subscriptions, invoices, and related payment operations
              when paid features are offered (contract, legal obligation, and
              legitimate interests).
            </p>
          </li>
          <li>
            <h2>Blockchain and Public Records</h2>
            <p>
              NumisTrail may store a cryptographic hash of a batch of Digital
              Record data on a Layer-2 network to support integrity and audit
              trails. We do not store plain personal data on-chain. Hashes may
              still be considered personal data if they can be linked to an
              individual when combined with other data.
            </p>
            <p>
              On-chain data is typically public and immutable and outside
              NumisTrail&apos;s control, which may limit our ability to delete
              such records.
            </p>
          </li>
          <li>
            <h2>Public Visibility and Sharing</h2>
            <p>
              Owners can designate a Digital Record as public or private. Public
              items may display ownership history and associated usernames to
              other users or visitors.
            </p>
            <p>
              Public views may also display trust state and selected transaction
              summaries (for example, metadata updates and digital certification
              events) to support transparency.
            </p>
            <p>
              Public collection views may display owner-created group names and
              the organization of public items within those groups.
            </p>
            <p>
              We may display shortened or masked identifiers in public views to
              reduce exposure, but public visibility remains a user-controlled
              choice.
            </p>
          </li>
          <li>
            <h2>Service Providers and Disclosures</h2>
            <p>
              We use service providers to operate the Service, including hosting
              and storage (Amazon Web Services) and transactional email delivery
              (Resend).
            </p>
            <p>
              If paid features are enabled, we may also use payment processors
              and billing providers to handle subscription charging, invoicing,
              and fraud checks.
            </p>
            <p>
              We may share data with certification entities, intermediaries, and
              marketplace partners when you use features that require them.
            </p>
            <p>
              We may disclose data to comply with law, protect our rights, or in
              connection with a merger, acquisition, or asset sale.
            </p>
          </li>
          <li>
            <h2>International Transfers</h2>
            <p>
              We process and store data in the United States (AWS us-east-1,
              Virginia) and may transfer data to other countries where our
              service providers operate. Where required, we use Standard
              Contractual Clauses or other lawful transfer mechanisms.
            </p>
          </li>
          <li>
            <h2>Data Retention</h2>
            <p>
              We retain personal data as long as necessary to provide the
              Service, comply with legal obligations, and resolve disputes. In
              general, account data is retained while your account is active and
              for a limited period after deactivation.
            </p>
            <p>
              Pending claim records may be retained for up to 12 months to allow
              users to reclaim access, then marked dormant until resolved.
            </p>
            <p>
              Security and legal acceptance records are retained for as long as
              needed to demonstrate compliance and protect the Service.
            </p>
            <p>
              Item edit, promotion, and certification attribution records may be
              retained as long as needed for auditability, fraud prevention,
              dispute handling, and legal compliance.
            </p>
            <p>
              Blockchain records and related metadata may be permanent and not
              removable.
            </p>
          </li>
          <li>
            <h2>Your Rights</h2>
            <p>
              Depending on your location, you may have the right to access,
              correct, delete, or restrict processing of your personal data; to
              object to certain processing; and to request data portability.
            </p>
            <p>
              If we rely on consent, you may withdraw it at any time without
              affecting the lawfulness of processing before withdrawal.
            </p>
            <p>
              EU/UK users may lodge a complaint with their local supervisory
              authority.
            </p>
            <p>To exercise rights, contact: privacy@numistrail.com.</p>
          </li>
          <li>
            <h2>Security</h2>
            <p>
              We use reasonable safeguards to protect personal data, but no
              system is fully secure.
            </p>
          </li>
          <li>
            <h2>Children</h2>
            <p>The Service is not intended for children under 18.</p>
          </li>
          <li>
            <h2>Changes</h2>
            <p>
              We may update this Policy. We will provide notice of material
              changes, and continued use constitutes acceptance.
            </p>
          </li>
          <li>
            <h2>Contact</h2>
            <p>privacy@numistrail.com</p>
          </li>
        </ol>
        <p className="legal-brand-notice">
          2026 NumisTrail (TM). A brand operated by Storychain Labs LLC.
        </p>
      </article>
    </div>
  );
}
